/**
 * Agent Capabilities tab (Sprint 267 — REQ-CAP-002 §4, ADR-113; ISS-567/572).
 *
 * Vista compacta (patrón "Skills del hive") + modal de configuración (patrón
 * "Skills scope"): decisión explícita del agente mediante check y **settings
 * por capacidad editables** (ISS-567) persistidos vía PATCH del agente
 * (`capabilities` + `capability_settings`, validados fail-closed en la API).
 *
 * Semántica del backend (agents.capabilities / agents.capability_settings):
 *   capabilities null  → sin decisión (hereda hive → tenant → plataforma)
 *   capabilities lista → pertenencia = habilitada explícitamente
 *   capability_settings {slug: {...}} → override del agente para esa capacidad
 * Al marcar la primera vez, la lista materializa el estado efectivo actual para
 * no cambiar el resto de capacidades. "Volver a heredar" limpia la decisión
 * (lista y settings).
 *
 * Self-contained (patrón AgentSkillsTab): persiste vía PATCH del agente, sin
 * pasar por el formulario general del editor.
 */

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button, Card, Spinner } from '@/components/ui';
import { Checkbox } from '@/components/ui/checkbox';
import { useI18n } from '@/i18n';
import { getErrorMessage } from '@/lib/api-client';
import { useUpdateAgent } from '@/hooks/use-agents';
import type { Agent, RestrictedAgent } from '@/features/agents/types';
import { CapabilitiesModal } from './CapabilitiesModal';
import { CapabilityCompactList } from './CapabilityCompactList';
import { CapabilitySettingsForm } from './CapabilitySettingsForm';
import { capabilityTitle } from './CapabilityRow';
import { useAgentCapabilities, useCapabilitiesCatalog } from './hooks';
import type { SettingsValues } from './types';

interface Props {
  agentId: string | undefined;
  /** Solo se usa `capabilities` / `capability_settings` (compatible con Agent y RestrictedAgent). */
  agent?: Agent | RestrictedAgent | null;
}

type AgentCapsSlice = {
  capabilities?: string[] | null;
  capability_settings?: Record<string, SettingsValues> | null;
} | null | undefined;

export function AgentCapabilitiesTab({ agentId, agent }: Props) {
  const { t } = useI18n();
  const catalog = useCapabilitiesCatalog();
  const resolved = useAgentCapabilities(agentId);
  const updateAgent = useUpdateAgent();

  const explicit = agent as AgentCapsSlice;
  const explicitList = explicit?.capabilities ?? null;
  const [list, setList] = useState<string[] | null>(explicitList);
  const [settingsDraft, setSettingsDraft] = useState<Record<string, SettingsValues>>(
    explicit?.capability_settings ?? {},
  );
  const [dirty, setDirty] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!dirty) {
      setList(explicitList);
      setSettingsDraft(explicit?.capability_settings ?? {});
    }
  }, [explicitList, explicit?.capability_settings, dirty]);

  const effective = useMemo(() => resolved.data ?? {}, [resolved.data]);
  const effectiveOn = useMemo(
    () =>
      new Set(
        Object.entries(effective)
          .filter(([, eff]) => eff.enabled)
          .map(([slug]) => slug),
      ),
    [effective],
  );

  // ISS-596: este useMemo DEBE ejecutarse en todos los renders — estaba tras
  // los returns tempranos (loading/error) y al pasar de "cargando" a "cargado"
  // React lanzaba #310 ("Rendered more hooks than during the previous render").
  const cleanedSettings = useMemo(() => {
    const out: Record<string, SettingsValues> = {};
    for (const [slug, values] of Object.entries(settingsDraft)) {
      if (values && Object.keys(values).length > 0) out[slug] = values;
    }
    return out;
  }, [settingsDraft]);

  if (!agentId) {
    return (
      <Card className="p-6 text-sm text-gray-500 dark:text-gray-400">
        {t('admin.capabilities.saveAgentFirst') ||
          'Save the agent first to configure its capabilities.'}
      </Card>
    );
  }

  if (catalog.isLoading || resolved.isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (catalog.isError || resolved.isError) {
    return (
      <Card className="p-6 text-sm text-red-600 dark:text-red-400">
        {t('admin.capabilities.loadError') || 'Could not load capability configuration.'}
      </Card>
    );
  }

  const isChecked = (slug: string): boolean =>
    list !== null ? list.includes(slug) : effectiveOn.has(slug);

  const toggle = (slug: string, checked: boolean) => {
    const base = list ?? Array.from(effectiveOn);
    const next = checked
      ? Array.from(new Set([...base, slug]))
      : base.filter((item) => item !== slug);
    setList(next);
    setDirty(true);
  };

  const setSetting = (slug: string, values: SettingsValues) => {
    setSettingsDraft((previous) => ({ ...previous, [slug]: values }));
    setDirty(true);
  };

  const resetToInherit = () => {
    setList(null);
    setSettingsDraft({});
    setDirty(true);
  };

  const save = async () => {
    try {
      await updateAgent.mutateAsync({
        id: agentId,
        capabilities: list,
        capability_settings: cleanedSettings,
      });
      setDirty(false);
      setModalOpen(false);
      toast.success(t('admin.capabilities.saved') || 'Capability configuration saved');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {t('admin.capabilities.title') || 'Capabilities'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {list === null
                ? t('admin.capabilities.inheritHint') ||
                  'No explicit agent decision: capabilities are inherited (hive → tenant → platform).'
                : t('admin.capabilities.explicitHint') ||
                  'This agent has an explicit capability decision. Uncheck to exclude; "Back to inherited" removes it.'}
            </p>
          </div>
          <Button onClick={() => setModalOpen(true)}>
            {t('admin.capabilities.manage') || 'Manage capabilities'}
          </Button>
        </div>
      </Card>

      <CapabilityCompactList catalog={catalog.data ?? []} effective={effective} />

      <CapabilitiesModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={t('admin.capabilities.title') || 'Capabilities'}
        description={t('admin.capabilities.manageDescription') || ''}
        catalog={catalog.data ?? []}
        effective={effective}
        dirty={dirty}
        renderControl={(entry) => (
          <Checkbox
            aria-label={capabilityTitle(t, entry)}
            checked={isChecked(entry.slug)}
            disabled={entry.platform_active === false}
            onCheckedChange={(checked) => toggle(entry.slug, checked === true)}
          />
        )}
        renderExtra={(entry) => {
          const hasSchema = !!(
            entry.config_schema?.properties &&
            Object.keys(entry.config_schema.properties).length > 0
          );
          if (!hasSchema) return undefined;
          return (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('admin.capabilities.agentSettings') ||
                  'Agent settings (override hive and tenant)'}
              </p>
              <CapabilitySettingsForm
                schema={entry.config_schema}
                values={settingsDraft[entry.slug] ?? {}}
                onChange={(values) => setSetting(entry.slug, values)}
              />
            </div>
          );
        }}
        footer={
          <>
            <Button
              variant="outline"
              onClick={resetToInherit}
              disabled={!dirty && list === null && Object.keys(cleanedSettings).length === 0}
            >
              {t('admin.capabilities.resetDecision') || 'Back to inherited'}
            </Button>
            <Button onClick={save} disabled={!dirty || updateAgent.isPending}>
              {updateAgent.isPending && <Spinner className="mr-2 h-4 w-4" />}
              {t('admin.capabilities.save') || 'Save'}
            </Button>
          </>
        }
      />
    </div>
  );
}
